import { Chip, CircularProgress, Dialog, Icon, IconButton, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useVisualizadorArquivos } from "context/VisualizadorArquivosContext";
import { useEffect, useRef, useState } from "react";
import { getFile, getFileNoDownload, postUpload } from "utils/WebService";
import { ESCURO } from "assets/coresTema";

// Como cada extensão é exibida: o backend devolve octet-stream, então o tipo vem do nome do arquivo
const MIME = {
    pdf: "application/pdf",
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", bmp: "image/bmp",
    txt: "text/plain", csv: "text/plain", log: "text/plain", json: "text/plain", xml: "text/plain", ret: "text/plain", rem: "text/plain",
};
const ICONE = { pdf: "picture_as_pdf", imagem: "image", texto: "description", email: "mail", outro: "insert_drive_file" };
const LIMITE_TEXTO = 200000;

const extensao = (nome) => (String(nome || "").split(".").pop() || "").toLowerCase();
const categoria = (nome) => {
    // E-mails salvos (Outlook .msg e .eml): o backend lê cabeçalho, corpo e anexos (/visualizador/email)
    if (["msg", "eml"].includes(extensao(nome))) return "email";
    const tipo = MIME[extensao(nome)];
    if (!tipo) return "outro";
    if (tipo === "application/pdf") return "pdf";
    return tipo.startsWith("image/") ? "imagem" : "texto";
};

// Corpo HTML do e-mail num iframe isolado: sem scripts (sandbox), sem buscar nada de fora (imagens externas,
// rastreadores) e com os links abrindo em nova aba
const POLITICA_EMAIL = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";
const htmlDoEmail = (html) =>
    `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${POLITICA_EMAIL}">`
    + `<base target="_blank"><style>body{font-family:Arial,sans-serif;font-size:13px;margin:12px;color:#222;background:#fff}</style></head>`
    + `<body>${html}</body></html>`;

const base64ParaBlob = (base64, tipo) => {
    const bytes = atob(base64);
    const array = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) array[i] = bytes.charCodeAt(i);
    return new Blob([array], { type: tipo || "application/octet-stream" });
};

const tamanhoLegivel = (bytes) => {
    if (bytes === null || bytes === undefined) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const LARGURA_PADRAO = 760;
const alturaPadrao = () => Math.max(360, Math.round(window.innerHeight * 0.78));
const limitar = (valor, minimo, maximo) => Math.min(Math.max(valor, minimo), maximo);

/**
 * Janela flutuante para ver anexos sem sair da tela: não bloqueia o que está atrás (dá para continuar usando o
 * sistema), arrasta pela barra, redimensiona pelo canto, minimiza e maximiza. PDF, imagem e texto aparecem dentro
 * dela; os demais tipos só baixam. Montada uma vez no App; as telas abrem com useVisualizadorArquivos().abrirArquivos.
 */
export default function VisualizadorArquivos() {
    const { estado, irPara, fechar, abrirArquivos } = useVisualizadorArquivos();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const arquivo = estado ? estado.arquivos[estado.indice] : null;
    const total = estado ? estado.arquivos.length : 0;

    const [conteudo, setConteudo] = useState({ carregando: false, url: null, texto: null, erro: null });
    const [posicao, setPosicao] = useState(null);
    const [tamanho, setTamanho] = useState({ w: LARGURA_PADRAO, h: 600 });
    const [minimizado, setMinimizado] = useState(false);
    const [maximizado, setMaximizado] = useState(false);
    const [arrastando, setArrastando] = useState(false);
    const arraste = useRef(null);
    const papelRef = useRef(null);

    // Posição inicial (canto direito) a cada abertura; fechada, volta ao padrão
    useEffect(() => {
        if (!estado) {
            setPosicao(null);
            setMinimizado(false);
            setMaximizado(false);
            return;
        }
        setMinimizado(false);
        setPosicao(atual => atual || { x: Math.max(16, window.innerWidth - LARGURA_PADRAO - 24), y: 56 });
        setTamanho(atual => (posicao ? atual : { w: LARGURA_PADRAO, h: alturaPadrao() }));
    }, [estado?.aberturas]);

    // Busca o arquivo com o login do usuário (ou usa o blob já em mãos, ex.: anexo de um e-mail) e mostra pela URL local
    useEffect(() => {
        if (!arquivo) return undefined;
        let cancelado = false;
        let urlLocal = null;
        setConteudo({ carregando: true, url: null, texto: null, email: null, erro: null });
        (arquivo.blob ? Promise.resolve(arquivo.blob) : getFileNoDownload(arquivo.url))
            .then(async (blob) => {
                if (cancelado) return;
                const tipado = new Blob([blob], { type: MIME[extensao(arquivo.nome)] || blob.type || "application/octet-stream" });
                urlLocal = URL.createObjectURL(tipado);
                let texto = null;
                let email = null;
                const cat = categoria(arquivo.nome);
                if (cat === "texto") {
                    texto = await tipado.text();
                    if (texto.length > LIMITE_TEXTO) texto = `${texto.substring(0, LIMITE_TEXTO)}\n\n[...] Arquivo grande: baixe para ver tudo.`;
                } else if (cat === "email") {
                    const formData = new FormData();
                    formData.append("file", new File([tipado], arquivo.nome || "email.msg"));
                    email = await postUpload("/visualizador/email", formData);
                }
                if (!cancelado) setConteudo({ carregando: false, url: urlLocal, texto, email, erro: null });
            })
            .catch((err) => {
                if (!cancelado) setConteudo({ carregando: false, url: null, texto: null, email: null, erro: err?.message || "Não foi possível carregar o arquivo." });
            });
        return () => {
            cancelado = true;
            if (urlLocal) URL.revokeObjectURL(urlLocal);
        };
    }, [arquivo, estado?.aberturas]);

    // Arrastar pela barra de título (o iframe do PDF não pode "engolir" o mouse durante o arraste)
    useEffect(() => {
        if (!arrastando) return undefined;
        const mover = (e) => {
            const largura = papelRef.current ? papelRef.current.offsetWidth : tamanho.w;
            setPosicao({
                x: limitar(e.clientX - arraste.current.dx, 120 - largura, window.innerWidth - 120),
                y: limitar(e.clientY - arraste.current.dy, 0, window.innerHeight - 44),
            });
        };
        const soltar = () => setArrastando(false);
        window.addEventListener("mousemove", mover);
        window.addEventListener("mouseup", soltar);
        return () => {
            window.removeEventListener("mousemove", mover);
            window.removeEventListener("mouseup", soltar);
        };
    }, [arrastando]);

    if (!estado || !arquivo || !posicao) return null;

    const cat = categoria(arquivo.nome);
    const lembrarTamanho = () => {
        if (papelRef.current && !minimizado && !maximizado) {
            setTamanho({ w: papelRef.current.offsetWidth, h: papelRef.current.offsetHeight });
        }
    };
    const iniciarArraste = (e) => {
        if (maximizado || e.button !== 0 || e.target.closest("button")) return;
        arraste.current = { dx: e.clientX - posicao.x, dy: e.clientY - posicao.y };
        setArrastando(true);
        e.preventDefault();
    };
    const baixarUrl = (url, nome) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = nome || "arquivo";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };
    const baixar = () => {
        if (conteudo.url) {
            baixarUrl(conteudo.url, arquivo.nome);
        } else if (arquivo.url) {
            getFile(arquivo.url, arquivo.nome).catch(() => null);
        }
    };
    const baixarBlob = (blob, nome) => {
        const url = URL.createObjectURL(blob);
        baixarUrl(url, nome);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    // Anexos do e-mail (as imagens do corpo já aparecem dentro dele). Abrem aqui mesmo: o e-mail fica como o 1º item
    // da navegação, para voltar a ele
    const anexosDoEmail = (conteudo.email?.anexos || []).filter(a => !a.inline || !conteudo.email?.html);
    const abrirAnexoDoEmail = (indice) => {
        const comConteudo = anexosDoEmail.map((a, i) => ({ ...a, i })).filter(a => a.base64);
        abrirArquivos(
            [{ url: arquivo.url, blob: arquivo.blob, nome: arquivo.nome, tipo: arquivo.tipo },
                ...comConteudo.map(a => ({ nome: a.nome, blob: base64ParaBlob(a.base64, a.tipo), tipo: "Anexo do e-mail" }))],
            1 + comConteudo.findIndex(a => a.i === indice));
    };

    const corBorda = darkMode ? "rgba(255,255,255,0.18)" : "#cfd4da";
    const corBarra = darkMode ? "#2F323B" : "#f0f2f5";
    const botao = (titulo, icone, onClick, disabled) => (
        <Tooltip title={titulo}>
            <span>
                <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={titulo} sx={{ color: darkMode ? "#fff" : "#344767" }}>
                    <Icon sx={{ fontSize: "1.15rem !important" }}>{icone}</Icon>
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Dialog
            open
            onClose={(e, motivo) => { if (motivo === "escapeKeyDown") fechar(); }}
            hideBackdrop
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            disableScrollLock
            // Não bloqueia a tela de trás: só a própria janela recebe o mouse
            sx={{ pointerEvents: "none", zIndex: 1350, "& .MuiDialog-container": { display: "block" } }}
            PaperProps={{
                ref: papelRef,
                elevation: 16,
                style: {
                    left: maximizado ? 8 : posicao.x,
                    top: maximizado ? 8 : posicao.y,
                    width: maximizado ? "calc(100vw - 16px)" : tamanho.w,
                    height: minimizado ? "auto" : (maximizado ? "calc(100vh - 16px)" : tamanho.h),
                },
                sx: {
                    pointerEvents: "auto",
                    position: "fixed",
                    m: 0,
                    maxWidth: "none",
                    maxHeight: "none",
                    minWidth: 340,
                    minHeight: minimizado ? 0 : 220,
                    resize: minimizado || maximizado ? "none" : "both",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    border: `1px solid ${corBorda}`,
                    background: darkMode ? ESCURO.painelEscuro : "#ffffff",
                },
            }}
        >
            {/* Barra de título: arrasta a janela; duplo clique maximiza */}
            <MDBox
                display="flex"
                alignItems="center"
                gap={1}
                px={1.25}
                py={0.5}
                onMouseDown={iniciarArraste}
                onDoubleClick={() => { lembrarTamanho(); setMaximizado(m => !m); setMinimizado(false); }}
                sx={{ background: corBarra, borderBottom: minimizado ? "none" : `1px solid ${corBorda}`, cursor: maximizado ? "default" : "move", userSelect: "none" }}
            >
                <Icon sx={{ color: darkMode ? "#90CAF9" : "#1A73E8" }}>{ICONE[cat]}</Icon>
                <MDBox minWidth={0} flex={1} display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="button" fontWeight="bold" color="text" title={arquivo.nome}
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {arquivo.nome || "Arquivo"}
                    </MDTypography>
                    {arquivo.tipo && (
                        <Chip size="small" label={arquivo.tipo}
                            sx={{ height: 18, fontSize: 10, background: darkMode ? "rgba(255,255,255,0.16)" : undefined, color: darkMode ? "#fff" : undefined }} />
                    )}
                </MDBox>
                {total > 1 && (
                    <MDBox display="flex" alignItems="center">
                        {botao("Anexo anterior", "chevron_left", () => irPara(estado.indice - 1), estado.indice === 0)}
                        <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>{estado.indice + 1} de {total}</MDTypography>
                        {botao("Próximo anexo", "chevron_right", () => irPara(estado.indice + 1), estado.indice === total - 1)}
                    </MDBox>
                )}
                {botao("Baixar", "download", baixar, conteudo.carregando)}
                {cat !== "outro" && cat !== "email" && botao("Abrir em nova aba", "open_in_new", () => conteudo.url && window.open(conteudo.url, "_blank"), !conteudo.url)}
                {botao(minimizado ? "Restaurar" : "Minimizar", minimizado ? "expand_less" : "remove", () => { lembrarTamanho(); setMinimizado(m => !m); })}
                {botao(maximizado ? "Restaurar tamanho" : "Maximizar", maximizado ? "close_fullscreen" : "open_in_full", () => { lembrarTamanho(); setMaximizado(m => !m); setMinimizado(false); })}
                {botao("Fechar", "close", fechar)}
            </MDBox>

            {!minimizado && (
                <MDBox flex={1} minHeight={0} display="flex" flexDirection="column" sx={{ background: darkMode ? ESCURO.cartao : "#fafafa" }}>
                    {conteudo.carregando ? (
                        <MDBox flex={1} display="flex" alignItems="center" justifyContent="center"><CircularProgress color="info" /></MDBox>
                    ) : conteudo.erro ? (
                        <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5} p={3} textAlign="center">
                            <Icon color="error" sx={{ fontSize: "2.2rem !important" }}>error_outline</Icon>
                            <MDTypography variant="button" color="text">{conteudo.erro}</MDTypography>
                        </MDBox>
                    ) : cat === "email" && conteudo.email ? (
                        <MDBox flex={1} minHeight={0} display="flex" flexDirection="column">
                            <MDBox px={1.5} py={1} sx={{ borderBottom: `1px solid ${corBorda}`, background: darkMode ? ESCURO.painel : "#ffffff" }}>
                                <MDTypography variant="button" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>
                                    {conteudo.email.assunto || "(sem assunto)"}
                                </MDTypography>
                                {[["De", conteudo.email.de], ["Para", conteudo.email.para], ["Cc", conteudo.email.cc], ["Data", conteudo.email.data]]
                                    .filter(([, v]) => v)
                                    .map(([rotulo, v]) => (
                                        <MDTypography key={rotulo} variant="caption" color="text" sx={{ display: "block", wordBreak: "break-word" }}>
                                            <strong>{rotulo}:</strong> {v}
                                        </MDTypography>
                                    ))}
                                {anexosDoEmail.length > 0 && (
                                    <MDBox mt={0.75} display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                        <Icon sx={{ fontSize: "1rem !important", color: darkMode ? "#c3c2b7" : "#7b809a" }}>attach_file</Icon>
                                        {anexosDoEmail.map((a, i) => (
                                            <MDBox key={`${a.nome}-${i}`} display="inline-flex" alignItems="center">
                                                <Chip size="small" variant="outlined" clickable={!!a.base64}
                                                    icon={<Icon>{a.base64 && categoria(a.nome) !== "outro" ? "visibility" : "insert_drive_file"}</Icon>}
                                                    label={`${a.nome}${a.tamanho ? ` (${tamanhoLegivel(a.tamanho)})` : ""}`}
                                                    title={a.base64 ? "Clique para visualizar" : "Anexo grande: baixe o e-mail para abrir"}
                                                    onClick={a.base64 ? () => abrirAnexoDoEmail(i) : undefined}
                                                    sx={{ color: darkMode ? "#fff" : undefined }} />
                                                {a.base64 && botao("Baixar anexo", "download", () => baixarBlob(base64ParaBlob(a.base64, a.tipo), a.nome))}
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                )}
                            </MDBox>
                            {conteudo.email.html ? (
                                <iframe title="Corpo do e-mail" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={htmlDoEmail(conteudo.email.html)}
                                    style={{ border: 0, width: "100%", flex: 1, pointerEvents: arrastando ? "none" : "auto", background: "#fff" }} />
                            ) : (
                                <MDBox flex={1} minHeight={0} overflow="auto" p={1.5}>
                                    <MDTypography component="pre" variant="caption" color="text"
                                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", m: 0 }}>
                                        {conteudo.email.texto || "(e-mail sem corpo)"}
                                    </MDTypography>
                                </MDBox>
                            )}
                        </MDBox>
                    ) : cat === "pdf" ? (
                        <iframe title={arquivo.nome} src={conteudo.url}
                            style={{ border: 0, width: "100%", flex: 1, pointerEvents: arrastando ? "none" : "auto", background: "#fff" }} />
                    ) : cat === "imagem" ? (
                        <MDBox flex={1} minHeight={0} overflow="auto" display="flex" alignItems="center" justifyContent="center" p={1}>
                            <img src={conteudo.url} alt={arquivo.nome} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        </MDBox>
                    ) : cat === "texto" ? (
                        <MDBox flex={1} minHeight={0} overflow="auto" p={1.5}>
                            <MDTypography component="pre" variant="caption" color="text"
                                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", m: 0 }}>
                                {conteudo.texto}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5} p={3} textAlign="center">
                            <Icon sx={{ fontSize: "2.4rem !important", color: darkMode ? "#c3c2b7" : "#7b809a" }}>insert_drive_file</Icon>
                            <MDTypography variant="button" color="text">
                                Não dá para mostrar arquivos .{extensao(arquivo.nome) || "?"} aqui. Baixe para abrir no programa do computador.
                            </MDTypography>
                            <MDButton variant="gradient" color="info" size="small" onClick={baixar}>
                                <Icon sx={{ mr: 0.5 }}>download</Icon>Baixar
                            </MDButton>
                        </MDBox>
                    )}
                </MDBox>
            )}
        </Dialog>
    );
}
